import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

// components
import LoadingScreen from 'components/LoadingScreen';
import ThemeShowcase from '../components/ThemeShowcase';
import ConsumptionRequestsGuide from '../components/documentation/ConsumptionRequestsGuide';
import MobeatPlatformGuide from '../components/documentation/MobeatPlatformGuide';

// guards
import ApplicationGuard from 'guards/ApplicationGuard';
import AuthGuard from 'guards/AuthGuard';
import FeatureGuard from 'guards/FeatureGuard';
import GuestGuard from 'guards/GuestGuard';
import OrganizationPermissionGuard from 'guards/OrganizationPermissionGuard';
import MobeatCustomerConsoleGuard from 'guards/MobeatCustomerConsoleGuard';

// layouts
import AppLayout from 'layouts/app';
import LogoOnlyLayout from 'layouts/LogoOnlyLayout';
import MainLayout from 'layouts/main';
import AdminHomeRedirect from 'components/AdminHomeRedirect';
import { PERMISSIONS } from 'utils/organizationPermissions';
import { PATH_PAGE } from 'routes/paths';
import { isDemoMode } from 'utils/demoEnvironment';

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  const isApp = pathname.includes('/app');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isApp && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed',
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const demoMode = isDemoMode();

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'register', element: <Navigate to={PATH_PAGE.requestDemo} replace /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    {
      path: 'admin',
      element: (
        <AuthGuard>
          <AppLayout currentPage='adminOverview' />
        </AuthGuard>
      ),
      children: [
        { element: <AdminHomeRedirect /> },
        {
          path: 'overview',
          element: (
            <MobeatCustomerConsoleGuard>
              <AdminOverview />
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'demo-leads',
          element: (
            <OrganizationPermissionGuard permissions={[PERMISSIONS.NAV_DEMO_LEADS]}>
              <DemoLeadsList />
            </OrganizationPermissionGuard>
          ),
        },
        {
          path: 'demo-leads/:leadId',
          element: (
            <OrganizationPermissionGuard permissions={[PERMISSIONS.NAV_DEMO_LEADS]}>
              <DemoLeadDetail />
            </OrganizationPermissionGuard>
          ),
        },
        {
          path: 'users',
          element: (
            <MobeatCustomerConsoleGuard>
              <OrganizationPermissionGuard permissions={[PERMISSIONS.ADMIN_CUSTOMER_ADMIN_ONLY]}>
                <UserList />
              </OrganizationPermissionGuard>
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'users/create',
          element: (
            <MobeatCustomerConsoleGuard>
              <OrganizationPermissionGuard permissions={[PERMISSIONS.ADMIN_CUSTOMER_ADMIN_ONLY]}>
                <UserCreate />
              </OrganizationPermissionGuard>
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'users/:userId/update',
          element: (
            <MobeatCustomerConsoleGuard>
              <OrganizationPermissionGuard permissions={[PERMISSIONS.ADMIN_CUSTOMER_ADMIN_ONLY]}>
                <UserCreate />
              </OrganizationPermissionGuard>
            </MobeatCustomerConsoleGuard>
          ),
        },
        { path: 'account', element: <Account /> },
        {
          path: 'applications/onboarding',
          element: (
            <MobeatCustomerConsoleGuard>
              <OrganizationPermissionGuard permissions={[PERMISSIONS.ADMIN_CUSTOMER_ADMIN_ONLY]}>
                <ApplicationOnboarding />
              </OrganizationPermissionGuard>
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'applications',
          element: (
            <MobeatCustomerConsoleGuard>
              <ApplicationList />
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'billing',
          element: (
            <MobeatCustomerConsoleGuard>
              <AdminSectionPlaceholder />
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'settings',
          element: (
            <MobeatCustomerConsoleGuard>
              <AdminSectionPlaceholder />
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'theme-showcase',
          element: (
            <MobeatCustomerConsoleGuard>
              <ThemeShowcase />
            </MobeatCustomerConsoleGuard>
          ),
        },
        {
          path: 'consumption-requests-guide',
          element: (
            <MobeatCustomerConsoleGuard>
              <ConsumptionRequestsGuide />
            </MobeatCustomerConsoleGuard>
          ),
        },
      ],
    },
    {
      path: 'applications/:appId',
      element: (
        <AuthGuard>
          <ApplicationGuard>
            <AppLayout currentPage='applications' />
          </ApplicationGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to='overview' replace={false} /> },
        { path: 'overview', element: <RevenueManagementOverview /> },
        { path: 'top-spenders', element: <TopSpenders /> },
        { path: 'users', element: <AppUserList /> },
        { path: 'users/:userId', element: <AppUserDetail /> },
        {
          path: 'transactions',
          children: [
            { element: <Navigate to='list' replace={true} /> },
            { path: 'list', element: <TransactionList /> },
            { path: ':transactionId', element: <TransactionDetail /> },
            { path: 'app-store-lookup', element: <AppStoreLookup /> },
          ],
        },

        {
          path: 'consumption-requests',
          children: [
            { element: <Navigate to='list' replace={true} /> },
            { path: 'list', element: <ConsumptionRequestList /> },
            { path: 'approved-transactions', element: <ApprovedConsumptionTransactions /> },
            { path: ':requestId', element: <ConsumptionRequestDetail /> },
          ],
        },

        {
          path: 'charts',
          children: [
            { element: <Navigate to='revenue' replace={false} /> },
            { path: 'explore', element: <AnalyticsHub /> },
            { path: 'revenue', element: <RevenueChart /> },
            // The two LTV charts are views of the Cohort LTV Explorer: the same people, the same
            // revenue, one calculation. Their own pages counted reactivations as new payers and
            // missed upgrade charges, so the numbers disagreed with the Explorer's.
            {
              path: 'ltv-per-paying-users',
              element: (
                <FeatureGuard feature='ltvChart'>
                  <Navigate
                    to='../cohort-explorer?cohort=new_paying_customers&measure=realized_ltv_per_customer&revenue=proceed'
                    replace
                  />
                </FeatureGuard>
              ),
            },
            {
              path: 'ltv-per-all-users',
              element: (
                <FeatureGuard feature='ltvChart'>
                  <Navigate
                    to='../cohort-explorer?cohort=new_customers&measure=realized_ltv_per_customer&revenue=proceed'
                    replace
                  />
                </FeatureGuard>
              ),
            },
            {
              path: 'ltv-prediction',
              element: (
                <FeatureGuard feature='ltvChart'>
                  <LTVPredictionChart />
                </FeatureGuard>
              ),
            },
            { path: 'mrr', element: <MRRChart /> },
            { path: 'arr', element: <ARRChart /> },
            { path: 'churn', element: <ChurnChart /> },
            { path: 'refund-rate', element: <RefundRateChart /> },
            { path: 'app-store-refund-requests', element: <AppStoreRefundRequestsChart /> },
            { path: 'play-store-cancel-reasons', element: <PlayStoreCancelReasonsChart /> },
            { path: 'cancellation-timing', element: <CancellationTimingChart /> },
            { path: 'funnel', element: <FunnelChart /> },
            { path: 'subscription-status', element: <SubscriptionStatusChart /> },
            { path: 'conversion-to-paying', element: <ConversionToPayingChart /> },
            { path: 'initial-conversion', element: <InitialConversionChart /> },
            { path: 'trial-conversion', element: <TrialConversionChart /> },
            { path: 'offer-conversion', element: <OfferConversionChart /> },
            { path: 'reactivation', element: <ReactivationChart /> },
            { path: 'active-trials', element: <ActiveTrialsChart /> },
            { path: 'new-trials', element: <NewTrialsChart /> },
            { path: 'active-trials-movement', element: <ActiveTrialsMovementChart /> },
            { path: 'active-subscriptions-movement', element: <ActiveSubscriptionsMovementChart /> },
            { path: 'active-subscribers', element: <ActiveSubscribersChart /> },
            { path: 'cohort-explorer', element: <CohortExplorer /> },
            { path: 'cohort-explorer-recoup', element: <CohortExplorerRecoup /> },
            { path: 'cohorted-initial-conversion', element: <CohortedInitialConversionChart /> },
            { path: 'cohorted-trial-conversion', element: <CohortedTrialConversionChart /> },
            { path: 'cohorted-intro-offer-conversion', element: <CohortedIntroOfferConversionChart /> },
            { path: 'cohorted-conversion-to-paying', element: <CohortedConversionToPayingChart /> },
            { path: 'cohorted-reactivation', element: <CohortedReactivationChart /> },
            { path: 'auto-renew-status', element: <AutoRenewStatusChart /> },
            {
              path: 'subscription-retention',
              element: (
                <FeatureGuard feature='subscriptionRetention'>
                  <SubscriptionRetentionChart />
                </FeatureGuard>
              ),
            },
            { path: 'subscription-events', element: <SubscriptionEventsChart /> },
            { path: 'ad-roi', element: <AdROIReport /> },
            { path: 'traffic-quality', element: <TrafficQualityChart /> },
            { path: 'cost-etl', element: <CostETLReport /> },
          ],
        },
        {
          path: 'products',
          element: <Products />,
        },
        { path: 'products/prices', element: <PriceSync /> },
        { path: 'products/new', element: <NewProduct /> },
        { path: 'products/:productId', element: <NewProduct update /> },
        {
          path: 'mailing-notifications',
          element: <FeatureGuard feature={'mailingNotifications'} />,
          children: [
            { element: <Navigate to='user-lists' replace={false} /> },
            { path: 'user-lists', element: <MNUserList /> },
            { path: 'user-lists/new', element: <MNUserListCreate /> },
            { path: 'user-lists/:listId', element: <MNUserListDetail /> },
            { path: 'user-lists/:listId/edit', element: <MNUserListEdit /> },
            { path: 'mailings', element: <AppMailing /> },
            { path: 'mailings/new', element: <NewAppMailing /> },
            { path: 'mailings/:mailingId/edit', element: <EditMailing /> },
            { path: 'push-templates', element: <PushTemplateList /> },
            { path: 'push-templates/new', element: <PushTemplateEditor /> },
            { path: 'push-templates/:templateId', element: <PushTemplateEditor /> },
            { path: 'campaigns', element: <CampaignList /> },
            { path: 'campaigns/new', element: <CampaignEditor /> },
            { path: 'campaigns/:campaignId', element: <CampaignEditor /> },
            { path: 'campaigns/:campaignId/report', element: <CampaignReport /> },
            { path: 'lifecycle', element: <LifecycleFlows /> },
            { path: 'lifecycle/:flowId', element: <LifecycleFlowPage /> },
            { path: 'lifecycle/:flowId/results', element: <LifecycleResults /> },
            { path: 'lifecycle/:flowId/steps/new', element: <LifecycleStepPage /> },
            { path: 'lifecycle/:flowId/steps/:stepId', element: <LifecycleStepPage /> },
            { path: 'lifecycle/:flowId/merge', element: <LifecycleStepPage /> },
            { path: 'lists', element: <PushLists /> },
          ],
        },
        { path: 'webhooks', element: <Webhooks /> },
        { path: 'webhooks/new', element: <NewWebhook /> },
        { path: 'webhooks/:webhookId/deliveries', element: <WebhookDeliveries /> },
        { path: 'ab-tests', element: <ABTestReport /> },
        { path: 'settings', element: <RevenueManagementSettings /> },
        { path: 'retention-messaging', element: <RetentionMessaging /> },
      ],
    },

    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to='/404' replace /> },
      ],
    },
    ...(demoMode
      ? [
          { path: '/', element: <Navigate to='/auth/login' replace /> },
          { path: 'request-demo', element: <Navigate to='/auth/login' replace /> },
          { path: 'mobeat-platform-guide', element: <Navigate to='/auth/login' replace /> },
        ]
      : [
          {
            path: '/',
            element: <MainLayout />,
            children: [
              { path: '/', element: <LandingPage /> },
              { path: 'mobeat-platform-guide', element: <MobeatPlatformGuide /> },
              { path: 'request-demo', element: <RequestDemo /> },
            ],
          },
        ]),
    { path: '*', element: <Navigate to={demoMode ? '/auth/login' : '/404'} replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('pages/authentication/Login')));
const ResetPassword = Loadable(lazy(() => import('pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('pages/authentication/VerifyCode')));

// Admin
const AdminOverview = Loadable(lazy(() => import('pages/admin/Overview')));
const UserList = Loadable(lazy(() => import('pages/admin/users/UserList')));
const UserCreate = Loadable(lazy(() => import('pages/admin/users/UserCreate')));
const Account = Loadable(lazy(() => import('pages/admin/account/Account')));
const ApplicationList = Loadable(lazy(() => import('pages/admin/applications/ApplicationList')));
const ApplicationOnboarding = Loadable(lazy(() => import('pages/admin/onboarding/ApplicationOnboarding')));
const AdminSectionPlaceholder = Loadable(lazy(() => import('pages/admin/AdminSectionPlaceholder')));
const DemoLeadsList = Loadable(lazy(() => import('pages/admin/DemoLeadsList')));
const DemoLeadDetail = Loadable(lazy(() => import('pages/admin/DemoLeadDetail')));

// Revenue Management
const RevenueManagementOverview = Loadable(lazy(() => import('pages/application/revenue-management/Overview')));
const AppUserList = Loadable(lazy(() => import('pages/application/user/UserList')));
const Webhooks = Loadable(lazy(() => import('pages/application/webhook/WebhookList')));
const NewWebhook = Loadable(lazy(() => import('pages/application/webhook/NewWebhook')));
const WebhookDeliveries = Loadable(lazy(() => import('pages/application/webhook/WebhookDeliveries')));
const Products = Loadable(lazy(() => import('pages/application/product/ProductList')));
const NewProduct = Loadable(lazy(() => import('pages/application/product/ProductForm')));
const AppUserDetail = Loadable(lazy(() => import('pages/application/user/UserDetail')));
const TransactionList = Loadable(
  lazy(() => import('pages/application/revenue-management/transaction/TransactionList')),
);
const TransactionDetail = Loadable(
  lazy(() => import('pages/application/revenue-management/transaction/TransactionDetail')),
);
const AnalyticsHub = Loadable(lazy(() => import('pages/application/revenue-management/charts/AnalyticsHub')));
const RevenueChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/RevenueChart')));
const MRRChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/MRRChart')));
const ARRChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/ARRChart')));
const ChurnChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/ChurnChart')));
const RefundRateChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/RefundRateChart')));
const AppStoreRefundRequestsChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/AppStoreRefundRequestsChart')),
);
const PlayStoreCancelReasonsChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/PlayStoreCancelReasonsChart')),
);
const CancellationTimingChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/CancellationTimingChart')),
);
const FunnelChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/FunnelChart')));
const SubscriptionStatusChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/SubscriptionStatusChart')),
);
const ConversionToPayingChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/ConversionToPayingChart')),
);
const InitialConversionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/InitialConversionChart')),
);
const TrialConversionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/TrialConversionChart')),
);
const OfferConversionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/OfferConversionChart')),
);
const ReactivationChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/ReactivationChart')),
);
const ActiveTrialsChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/ActiveTrialsChart')));
const NewTrialsChart = Loadable(lazy(() => import('pages/application/revenue-management/charts/NewTrialsChart')));
const ActiveTrialsMovementChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/ActiveTrialsMovementChart')),
);
const ActiveSubscriptionsMovementChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/ActiveSubscriptionsMovementChart')),
);
const ActiveSubscribersChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/ActiveSubscribersChart')),
);
const CohortExplorer = Loadable(lazy(() => import('pages/application/revenue-management/charts/CohortExplorer')));
const CohortExplorerRecoup = Loadable(lazy(() => import('pages/application/revenue-management/charts/CohortExplorerRecoup')));
const CohortedInitialConversionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/CohortedInitialConversionChart')),
);
const CohortedTrialConversionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/CohortedTrialConversionChart')),
);
const CohortedIntroOfferConversionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/CohortedIntroOfferConversionChart')),
);
const CohortedConversionToPayingChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/CohortedConversionToPayingChart')),
);
const CohortedReactivationChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/CohortedReactivationChart')),
);
const AutoRenewStatusChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/AutoRenewStatusChart')),
);
const SubscriptionRetentionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/SubscriptionRetentionChart')),
);
const SubscriptionEventsChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/SubscriptionEventsChart')),
);
const AdROIReport = Loadable(lazy(() => import('pages/application/revenue-management/charts/AdROIReport')));
const TrafficQualityChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/TrafficQualityChart'))
);
const CostETLReport = Loadable(lazy(() => import('pages/application/revenue-management/charts/CostETLReport')));

// Monetization
const MNUserList = Loadable(lazy(() => import('pages/application/monetization/mn_user_list/MNUserList')));
const MNUserListCreate = Loadable(lazy(() => import('pages/application/monetization/mn_user_list/MNUserListCreate')));
const MNUserListEdit = Loadable(lazy(() => import('pages/application/monetization/mn_user_list/MNUserListEdit')));
const MNUserListDetail = Loadable(lazy(() => import('pages/application/monetization/mn_user_list/MNUserListDetail')));
const AppMailing = Loadable(lazy(() => import('pages/application/monetization/mailing/MailingList')));
const NewAppMailing = Loadable(lazy(() => import('pages/application/monetization/mailing/NewMailing')));

const RevenueManagementSettings = Loadable(lazy(() => import('pages/application/revenue-management/Settings')));
const RetentionMessaging = Loadable(lazy(() => import('pages/application/retention-messaging/RetentionMessaging')));

// Common
const LandingPage = Loadable(lazy(() => import('pages/LandingPage')));
const RequestDemo = Loadable(lazy(() => import('pages/RequestDemo')));
const Page500 = Loadable(lazy(() => import('pages/Page500')));
const NotFound = Loadable(lazy(() => import('pages/Page404')));
const Page403 = Loadable(lazy(() => import('pages/Page403')));

// Add the new route
const EditMailing = Loadable(lazy(() => import('pages/application/monetization/mailing/EditMailing')));
const PushTemplateList = Loadable(lazy(() => import('pages/application/monetization/push/TemplateList')));
const PushTemplateEditor = Loadable(lazy(() => import('pages/application/monetization/push/TemplateEditor')));
const CampaignList = Loadable(lazy(() => import('pages/application/monetization/push/CampaignList')));
const CampaignEditor = Loadable(lazy(() => import('pages/application/monetization/push/CampaignEditor')));
const CampaignReport = Loadable(lazy(() => import('pages/application/monetization/push/CampaignReport')));
const LifecycleFlows = Loadable(lazy(() => import('pages/application/monetization/push/LifecycleFlows')));
const LifecycleFlowPage = Loadable(lazy(() => import('pages/application/monetization/push/LifecycleFlowPage')));
const LifecycleResults = Loadable(lazy(() => import('pages/application/monetization/push/LifecycleResults')));
const LifecycleStepPage = Loadable(lazy(() => import('pages/application/monetization/push/LifecycleStepPage')));
const PushLists = Loadable(lazy(() => import('pages/application/monetization/push/Lists')));

// Add import for consumption requests components
const ConsumptionRequestList = Loadable(
  lazy(() => import('../pages/application/revenue-management/consumption-request/ConsumptionRequestList')),
);
const ConsumptionRequestDetail = Loadable(
  lazy(() => import('../pages/application/revenue-management/consumption-request/ConsumptionRequestDetail')),
);

const ApprovedConsumptionTransactions = Loadable(
  lazy(() => import('../pages/application/revenue-management/consumption-request/ApprovedConsumptionTransactions')),
);

const AppStoreLookup = Loadable(
  lazy(() => import('../pages/application/revenue-management/transaction/AppStoreLookup')),
);
const PriceSync = Loadable(lazy(() => import('../pages/application/revenue-management/PriceSync')));
const LTVPredictionChart = Loadable(
  lazy(() => import('pages/application/revenue-management/charts/LTVPredictionChart')),
);
const TopSpenders = Loadable(lazy(() => import('../pages/application/revenue-management/TopSpenders')));

// A/B Test
const ABTestReport = Loadable(lazy(() => import('../pages/application/ab-test/ABTestReport')));
